/**
 * Marketing's ad tracking: the Meta Pixel (which ad brought a visitor, and did
 * they sign up) and Microsoft Clarity (session recordings and heatmaps).
 *
 * Both run on the signup page only, and only in production. This app renders
 * candidate names, resumes and interview reports, which must never reach a
 * third party, so neither script is ever loaded anywhere else:
 *
 *   - loadSignupTracking() is called by the signup page alone.
 *   - Leaving /signup inside the app reloads the page (leaveSignupTracking,
 *     called by App on every route change), which unloads both scripts.
 *   - The pixel's automatic tracking is off: no automatic page views on route
 *     changes, no button clicks or page details. It sends only PageView on
 *     load and the two events below.
 *
 * Meta events:
 *   PageView              someone landed on /signup
 *   Lead                  step 1 accepted (code or setup link emailed),
 *                         event ID lead_<pending signup id>
 *   CompleteRegistration  account created, event ID signup_<user id>
 * Each carries the match keys known at that point (email; then phone, user ID
 * and the phone number's country), passed to the pixel just before the event.
 * The pixel hashes them. A phone signup finishes on a laptop from an emailed
 * link; the backend adds the ad click's fbclid to that link so the pixel there
 * still ties the signup to the ad.
 * Our own team (nsquarexperts.com, geniehire.ai) never sends Lead or
 * CompleteRegistration, so test signups do not count as ad results.
 */
import { ENVIRONMENT, isInternalEmail } from "./analytics";

const META_PIXEL_ID = "1437918643942297";
const CLARITY_ID = "vb30e6lsg2";

const SIGNUP_PATH = "/signup";
const ENABLED = ENVIRONMENT === "production";

let loaded = false;

const addScript = (src) => {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const loadMetaPixel = () => {
  // Meta's own snippet: queues calls until fbevents.js arrives
  if (!window.fbq) {
    const fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    addScript("https://connect.facebook.net/en_US/fbevents.js");
  }
  // No automatic page view per route change: only the one below
  window.fbq.disablePushState = true;
  // No automatic button-click and page-detail events
  window.fbq("set", "autoConfig", false, META_PIXEL_ID);
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
};

const loadClarity = () => {
  // Clarity's own snippet: queues calls until the tag arrives
  if (window.clarity) return;
  window.clarity = function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };
  addScript(`https://www.clarity.ms/tag/${CLARITY_ID}`);
};

/** The signup page calls this once it is on screen. */
export function loadSignupTracking() {
  if (!ENABLED || loaded || window.location.pathname !== SIGNUP_PATH) return;
  loaded = true;
  try {
    loadMetaPixel();
    loadClarity();
  } catch {
    /* blocked or broken: tracking must never get in the way of signing up */
  }
}

/** App calls this on every route change: anywhere but /signup, after the
 *  scripts were loaded, reload so they are gone before the next page shows. */
export function leaveSignupTracking(pathname) {
  if (loaded && pathname !== SIGNUP_PATH) window.location.reload();
}

/**
 * Send Lead or CompleteRegistration for this work email. Resolves once the
 * event has had time to leave, so a page change right after cannot cut it off;
 * immediately when nothing is sent.
 *
 * eventId:  Meta merges copies of one event that share it
 * match:    { phone: "+14155550100", userId, country: "US" }, whichever are known
 * data:     custom data for the event
 */
export function trackSignupConversion(event, email, { eventId, match = {}, data } = {}) {
  if (!loaded || !window.fbq || isInternalEmail(email)) return Promise.resolve();
  try {
    const userData = {
      em: String(email || "").trim().toLowerCase(),
      // Digits with the country code: the number is stored in international
      // format, so its own country's code is already there
      ph: String(match.phone || "").replace(/\D/g, ""),
      external_id: match.userId ? String(match.userId) : "",
      country: String(match.country || "").toLowerCase(),
    };
    Object.keys(userData).forEach((key) => {
      if (!userData[key]) delete userData[key];
    });
    window.fbq("init", META_PIXEL_ID, userData);
    window.fbq("track", event, data || {}, eventId ? { eventID: eventId } : undefined);
  } catch {
    return Promise.resolve();
  }
  return new Promise((resolve) => setTimeout(resolve, 800));
}
